/* eslint-disable max-lines */
import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import { type UseModalState, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { invalidateClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/invalidateClipboardScoreProfile";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useGeofenceContext } from "@src/appV2/Location/geofence/useGeofence";
import { useArrivalTimeConfig } from "@src/appV2/redesign/ArrivalTime/useArrivalTimeConfig";
import { SimpleAlert } from "@src/appV2/redesign/components/SimpleAlert";
import { doesShiftInProgressAllowBreakAction } from "@src/appV2/redesign/utils/doesShiftInProgressAllowBreakAction";
import { getShiftWorkDurationInHours } from "@src/appV2/redesign/utils/getShiftWorkDuration";
import { getEffectiveGeofenceArrival } from "@src/appV2/redesign/WorkerShifts/MissedPunches/utils/getEffectiveGeofenceArrival";
import { ClockOutBottomSheet } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/ClockOutBottomSheet";
import { type TimekeepingAction } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/constants";
import { useTimekeepingActionTimer } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/hooks/useTimekeepingActionTimer";
import { ExtraWorkedTimeRequestStatus } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import {
  DEFAULT_MESH_AGENCY_ID,
  DEFAULT_MESH_AGENCY_NAME,
  DEFAULT_MESH_TIME_TO_ALLOW_SKIP_IN_SEC,
} from "@src/appV2/Shifts/Shift/ShiftState/api/useGetShiftState";
import { getExtendShiftConfig } from "@src/appV2/Shifts/Shift/ShiftState/getExtendShiftConfig";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import {
  ExtraWorkedTimeType,
  type NfcTagRequestStatus,
  type Shift,
  type WorkerShift,
} from "@src/appV2/Shifts/Shift/types";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { isEqual, min, parseISO } from "date-fns";
import { useCallback } from "react";

import { useCreateFullShiftExtraWorkedTimeRequest } from "../../api/useCreateFullShiftExtraWorkedTimeRequest";
import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import { usePatchFullShiftExtraWorkedTimeRequest } from "../../api/usePatchFullShiftExtraWorkedTimeRequest";
import { ShiftStage } from "../../api/useRecordShiftTimekeepingAction";
import { useAttendanceVerificationMethod } from "../../Timekeeping/AttendanceVerificationMethods/useAttendanceVerificationMethods";
import { BreakActionBottomSheet } from "../../Timekeeping/TimekeepingActions/BreakActionBottomSheet";
import { ClockOutTimeclockComplianceBottomSheet } from "../../Timekeeping/TimekeepingActions/ClockOutTimeclockComplianceBottomSheet";
import { ClockOutTimekeepingIntegrationBottomSheet } from "../../Timekeeping/TimekeepingActions/ClockOutTimekeepingIntegrationBottomSheet";
import { ClockOutWithVideoValidationBottomSheet } from "../../Timekeeping/TimekeepingActions/ClockOutWithVideoValidationBottomSheet";
import { getHasWorkerClockedInOnTime } from "../../Timekeeping/TimekeepingActions/utils/getHasWorkerClockedInOnTime";
import { isMandatoryBreakRequired } from "../../Timekeeping/Timesheet/utils/isMandatoryBreakRequired";
import { AcceptNonInstantPayBottomSheet } from "../components/AcceptNonInstantPayBottomSheet";
import { TooFarAwayBottomSheet } from "../components/TooFarAwayBottomSheet";
import { AdjustTimekeepingBottomSheet } from "./AdjustTimekeepingBottomSheet";
import {
  type ProvisionalExtraWorkedTimeRequest,
  useGetProvisionalExtraWorkedTimeRequest,
} from "./api/useGetProvisionalExtraWorkedTimeRequest";
import { InShiftAdjustmentTimesheetReviewAlert } from "./components/InShiftAdjustmentTimesheetReviewAlert";
import { ExtendShiftBottomSheet } from "./Dialogs/ExtendShiftBottomSheet";
import { ExtendShiftSuccessBottomSheet } from "./Dialogs/ExtendShiftSuccessBottomSheet";
import { useOnClockOut } from "./hooks/useOnClockOut";
import { ShiftCircleProgress } from "./ShiftCircleProgress";
import { useShouldShowSuggestInstantPay } from "./SuggestInstantPay/hooks/useShouldShowSuggestInstantPay";
import { SuggestInstantPayBottomSheet } from "./SuggestInstantPay/SuggestInstantPayBottomSheet";
import { TimekeepingActions } from "./TimekeepingActions/TimekeepingActions";
import { TimekeepingActivityList } from "./TimekeepingActivityList";
import { ClockOutTouchpointBottomSheet } from "./TouchPoint/TouchpointActions/ClockOutTouchpointBottomSheet";
import { getIsShiftEndingSoon } from "./utils/getIsShiftEndingSoon";

interface TimekeepingFocusSubPageProps {
  shift: WorkerShift;
  shiftState: ShiftStateData;
  workplaceImage?: string;
  workplaceName?: string;
  currentDate: Date;
  clockOutModalState: UseModalState;
}

function shouldShowBreakActivity(shift: Shift) {
  const doesFacilityRequireMandatoryBreak = shift.facility.requiresLunchBreak ?? false;

  // If facility requires mandatory breaks, we don't show break actions
  // since breaks will be automatically added
  if (doesFacilityRequireMandatoryBreak) {
    return false;
  }

  // Only show break actions if shift duration requires a break
  // and facility doesn't have mandatory breaks
  return doesShiftInProgressAllowBreakAction(shift, doesFacilityRequireMandatoryBreak);
}

function findClockInAdjustmentRequest(
  requests: ProvisionalExtraWorkedTimeRequest[],
  shiftEndDate: Date
): ProvisionalExtraWorkedTimeRequest | undefined {
  return requests.find(
    (request) =>
      !isDefined(request.attributes.end) &&
      !isEqual(parseISO(request.attributes.start), shiftEndDate)
  );
}

// NOTE: This feature is not yet in production. The current heuristic (!end) cannot
// distinguish shift extensions from clock-in adjustments. A subsequent PR will add a
// reason-map field on the backend to enable reliable classification (TEM-2429 follow-up).
function findShiftExtensionRequest(
  requests: ProvisionalExtraWorkedTimeRequest[]
): ProvisionalExtraWorkedTimeRequest | undefined {
  return requests.find((request) => !isDefined(request.attributes.end));
}

export function TimekeepingFocusSubPage(props: TimekeepingFocusSubPageProps) {
  const { shift, shiftState, workplaceImage, workplaceName, currentDate, clockOutModalState } =
    props;

  const {
    firstName,
    lastName,
    stage: workerStage,
    substage: workerSubstage,
    userId: workerId,
  } = useDefinedWorker();
  const { inside } = useGeofenceContext();
  const { showErrorToast } = useToast();
  const { mutateAsync: markNonInstant, isLoading: isConvertingToNonInstantPay } =
    useMarkShiftNonInstant();

  const workplaceId = shift.facilityId!;

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);

  const shouldShowSuggestInstantPay = useShouldShowSuggestInstantPay({
    isInstantPayShift: shift.isInstantPay,
  });

  const isClipboardScoreFreezeEnabled = clipboardScoreProfile?.scoreFreeze?.enabled;
  const clipboardScoreFreezeReason = clipboardScoreProfile?.scoreFreeze?.enabled
    ? clipboardScoreProfile?.scoreFreeze?.reason
    : undefined;

  const adjustClockInModalState = useModalState();
  const breakStartModalState = useModalState();
  const breakEndModalState = useModalState();
  const skipBreakModalState = useModalState();
  const tooFarAwayModalState = useModalState();
  const acceptNonInstantPayModalState = useModalState();
  const suggestInstantPayModalState = useModalState();
  const shiftEndsSoonModalState = useModalState();
  const extendShiftSuccessModalState = useModalState();

  const { allowedExtendedTimeInMinutes } = getExtendShiftConfig(
    shiftState.metadata.extendShiftConfig
  );
  const isExtendShiftEnabled =
    isDefined(allowedExtendedTimeInMinutes) && allowedExtendedTimeInMinutes > 0;

  const isShiftEndingSoon = getIsShiftEndingSoon({
    shiftStartDate: parseISO(shift.start),
    shiftEndDate: parseISO(shift.end),
    now: currentDate,
  });

  const { isMissedPunchRequestEnabled = false, isMissedPunchRequestEnabledV2 } =
    shiftState.metadata;

  const { data: provisionalExtraWorkedTimeRequest } = useGetProvisionalExtraWorkedTimeRequest(
    shift._id,
    { enabled: isMissedPunchRequestEnabledV2 || isExtendShiftEnabled }
  );
  const shiftEndDate = parseISO(shift.end);
  const provisionalRequests = provisionalExtraWorkedTimeRequest?.data ?? [];

  const existingShiftExtensionRequest = findShiftExtensionRequest(provisionalRequests);

  const existingProvisionalExtraWorkedTimeRequest = findClockInAdjustmentRequest(
    provisionalRequests,
    shiftEndDate
  );
  const adjustedClockInDate = isDefined(existingProvisionalExtraWorkedTimeRequest)
    ? parseISO(existingProvisionalExtraWorkedTimeRequest.attributes.start)
    : undefined;

  const { mutate: createExtraWorkedTimeRequest, isLoading: isCreatingShiftExtension } =
    useCreateFullShiftExtraWorkedTimeRequest();
  const { mutate: patchExtraWorkedTimeRequest } = usePatchFullShiftExtraWorkedTimeRequest();

  const showBreakActivity = shouldShowBreakActivity(shift);
  const showMandatoryBreakReminder = isMandatoryBreakRequired({
    requiresLunchBreak: shift.facility.requiresLunchBreak ?? false,
    clockInTime: isDefined(shift.clockInOut?.start) ? parseISO(shift.clockInOut.start) : undefined,
    clockOutTime: isDefined(shift.clockInOut?.end) ? parseISO(shift.clockInOut.end) : undefined,
  });

  const workplaceTimezone = shift.facility?.tmz;

  const isTimesheetEditDisabled = shiftState?.metadata.isTimeSheetEditDisabled ?? false;

  const { useExpectedArrivalTime } = useArrivalTimeConfig();

  const enforceNfcTimesheetBoundsFlag = useCbhFlag(CbhFeatureFlag.ENFORCE_NFC_TIMESHEET_BOUNDS, {
    defaultValue: false,
  });

  const clockInDate = isDefined(shift.clockInOut?.start)
    ? parseISO(shift.clockInOut.start)
    : undefined;

  const geofenceEntryTime = isDefined(shift.geofence?.entry)
    ? parseISO(shift.geofence.entry)
    : undefined;

  const earliestPlausibleArrivalAt = isDefined(shift.attendance?.earliestPlausibleArrivalAt)
    ? parseISO(shift.attendance.earliestPlausibleArrivalAt)
    : undefined;

  const { isEnforceGeofenceTimestampsEnabled = false } = shiftState.metadata;

  const { value: arrivalTimestamp } = getEffectiveGeofenceArrival(
    geofenceEntryTime,
    earliestPlausibleArrivalAt,
    isEnforceGeofenceTimestampsEnabled
  );

  const arrivedDate =
    isDefined(arrivalTimestamp) && isDefined(clockInDate)
      ? min([arrivalTimestamp, clockInDate])
      : clockInDate;

  const breakStartDate = isDefined(shift.lunchInOut?.start)
    ? parseISO(shift.lunchInOut.start)
    : undefined;
  const breakEndDate = isDefined(shift.lunchInOut?.end)
    ? parseISO(shift.lunchInOut.end)
    : undefined;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: shift.facility.userId ?? "",
  });

  const {
    attendanceVerificationMethods,
    isMethodEnabled,
    externalTimeclockCredentials,
    getMethodConfig,
  } = useAttendanceVerificationMethod({
    shiftStateMetadata: shiftState?.metadata,
  });
  const nfcMethodConfig = getMethodConfig(AttendanceVerificationMethod.NFC_TAG);
  const nfcTagRequests =
    isDefined(nfcMethodConfig) && "tags" in nfcMethodConfig
      ? (nfcMethodConfig.tags ?? []).map((tag) => ({
          tagStatus: tag.status as NfcTagRequestStatus,
          tagLocation: tag.location,
        }))
      : [];

  const enforceNfcTimesheetBounds =
    enforceNfcTimesheetBoundsFlag && isMethodEnabled(AttendanceVerificationMethod.NFC_TAG);

  const [attendanceVerificationMethod] = attendanceVerificationMethods;

  const timekeepingMethod =
    attendanceVerificationMethod?.type ?? AttendanceVerificationMethod.GEOFENCE;

  const timekeepingAction: TimekeepingAction = "clock-out";

  const queryClient = useQueryClient();

  const { startTimekeepingActionTracking, completeTimekeepingActionTracking } =
    useTimekeepingActionTimer({
      action: timekeepingAction,
      shiftId: shift._id,
      workerId,
      workplaceId: shift.facility.userId,
      timekeepingMethod,
    });

  const handleTimekeepingActionCompleted = useCallback(
    (props?: { nonInstantPayReason?: string }) => {
      completeTimekeepingActionTracking(props);
      invalidateClipboardScoreProfile(queryClient, workerId);
    },
    [completeTimekeepingActionTracking, queryClient, workerId]
  );

  const onTimekeepingActionFailed = useCallback(
    ({ error }: { error: string }) => {
      logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_ACTION_FAILED, {
        action: timekeepingAction,
        shiftId: shift._id,
        workerId,
        workplaceId: shift.facility.userId,
        timekeepingMethod,
        error,
      });
    },
    [shift._id, workerId, shift.facility.userId, timekeepingMethod]
  );

  const { onClockOut, onProceedToClockOut } = useOnClockOut({
    shift,
    clockOutModalState,
    tooFarAwayModalState,
    inside,
    onClockOutInitiated: startTimekeepingActionTracking,
    onWillOpenClockOutModal: () => {
      if (isMethodEnabled(AttendanceVerificationMethod.MESH_INTEGRATION)) {
        logEvent(APP_V2_USER_EVENTS.TOUCHPOINT_CLOCK_OUT_STARTED, {
          shiftId: shift._id,
          type: "clock-out",
        });
      }
    },
    shouldShowSuggestInstantPay,
    onSuggestInstantPay: () => {
      suggestInstantPayModalState.openModal();
    },
  });

  const handleAcceptNonInstantPay = async () => {
    try {
      await markNonInstant({
        shiftId: shift._id,
        reason: ShiftMarkedNonIpReasons.NO_LOCATION,
      });

      handleTimekeepingActionCompleted({ nonInstantPayReason: "NO_LOCATION" });
      acceptNonInstantPayModalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);
    }
  };

  const hasWorkerClockedInOnTime = getHasWorkerClockedInOnTime({
    shiftStartDate: parseISO(shift.start),
    clockInTime: clockInDate,
  });

  const isSecondShiftInDouble = isDefined(shift)
    ? Boolean(shift.isAfterAnotherShift) || Boolean(shift.isSecondShiftInDouble)
    : false;

  const targetArrivalTime = isDefined(shift?.computedArrival?.targetArrivalTime)
    ? parseISO(shift.computedArrival.targetArrivalTime)
    : undefined;

  const shiftBookedAt = isDefined(shift?.bookedAt) ? parseISO(shift.bookedAt) : undefined;

  const handleEditClockIn = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_EDIT_CLOCK_IN_TAPPED, {
      shiftId: shift._id,
      workerId,
      workplaceId: shift.facility.userId,
    });
    adjustClockInModalState.openModal();
  }, [shift._id, workerId, shift.facility.userId, adjustClockInModalState]);

  function getClockOutBottomSheet() {
    if (
      attendanceVerificationMethod?.type === AttendanceVerificationMethod.UKG_INTEGRATION ||
      attendanceVerificationMethod?.type ===
        AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION
    ) {
      return (
        <ClockOutTimekeepingIntegrationBottomSheet
          modalState={clockOutModalState}
          config={attendanceVerificationMethod}
          workerCredentials={{ badgeNumber: externalTimeclockCredentials.badgeNumber ?? "" }}
          shiftId={shift._id}
          workerId={shift.agentId}
          workplaceId={shift.facility.userId ?? ""}
          workplaceName={shift.facility.name}
          onTimekeepingActionCompleted={handleTimekeepingActionCompleted}
          onTimekeepingActionFailed={onTimekeepingActionFailed}
        />
      );
    }
    if (attendanceVerificationMethod?.type === AttendanceVerificationMethod.MESH_INTEGRATION) {
      return (
        <ClockOutTouchpointBottomSheet
          isNfcEnabled={isMethodEnabled(AttendanceVerificationMethod.NFC_TAG)}
          nfcTagRequests={nfcTagRequests}
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          modalState={clockOutModalState}
          shift={shift}
          workerId={shift.agentId}
          workplaceId={shift.facility.userId ?? ""}
          workplaceName={shift.facility.name}
          kioskLocation={attendanceVerificationMethod.kioskLocation ?? ""}
          timeToAllowSkipInSec={
            attendanceVerificationMethod.timeToAllowSkipInSec ??
            DEFAULT_MESH_TIME_TO_ALLOW_SKIP_IN_SEC
          }
          qrCodeProps={{
            shiftId: shift._id,
            agentId: shift.agentId,
            agentFirstName: firstName ?? "",
            agentLastName: lastName ?? "",
            agentType: shift.agentReq,
            facilityId: shift.facilityId,
            facilityName: shift.facility.name,
            agencyId: attendanceVerificationMethod.asAgencyId ?? DEFAULT_MESH_AGENCY_ID,
            agencyName: attendanceVerificationMethod.asAgencyName ?? DEFAULT_MESH_AGENCY_NAME,
            punchType: "clock-out",
          }}
          onTimekeepingActionCompleted={handleTimekeepingActionCompleted}
          onTimekeepingActionFailed={onTimekeepingActionFailed}
        />
      );
    }

    if (attendanceVerificationMethod?.type === AttendanceVerificationMethod.TAKE_A_VIDEO) {
      return (
        <ClockOutTimeclockComplianceBottomSheet
          modalState={clockOutModalState}
          shift={shift}
          attendanceVerificationMethod={attendanceVerificationMethod}
          workerCredentials={{ pin: externalTimeclockCredentials.pin ?? "" }}
          workerId={shift.agentId}
          workplaceId={shift.facility.userId ?? ""}
          workplaceName={shift.facility.name}
          isNfcEnabled={isMethodEnabled(AttendanceVerificationMethod.NFC_TAG)}
          nfcTagRequests={nfcTagRequests}
          shiftState={shiftState}
          onTimekeepingActionCompleted={handleTimekeepingActionCompleted}
          onTimekeepingActionFailed={onTimekeepingActionFailed}
        />
      );
    }

    if (attendanceVerificationMethod?.type === AttendanceVerificationMethod.AI_TAKE_A_VIDEO) {
      return (
        <ClockOutWithVideoValidationBottomSheet
          modalState={clockOutModalState}
          shift={shift}
          attendanceVerificationMethod={attendanceVerificationMethod}
          workerCredentials={{ pin: externalTimeclockCredentials.pin ?? "" }}
          workerId={shift.agentId}
          workplaceId={shift.facility.userId ?? ""}
          workplaceName={shift.facility.name}
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          onTimekeepingActionCompleted={handleTimekeepingActionCompleted}
          onTimekeepingActionFailed={onTimekeepingActionFailed}
        />
      );
    }

    return (
      <ClockOutBottomSheet
        modalState={clockOutModalState}
        shiftId={shift._id}
        shiftEndDate={shiftEndDate}
        workerId={shift.agentId}
        workplaceId={shift.facility.userId ?? ""}
        workplaceName={shift.facility.name}
        isNfcClockOutEnabled={
          attendanceVerificationMethod?.type === AttendanceVerificationMethod.NFC_TAG
        }
        nfcTagRequests={nfcTagRequests}
        shiftDuration={getShiftWorkDurationInHours(
          { start: shift.start, end: shift.end },
          shift.facility.requiresLunchBreak ?? false,
          mandatoryBreakDurationInMinutes
        )}
        currentDate={currentDate}
        isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
        onTimekeepingActionCompleted={handleTimekeepingActionCompleted}
        onTimekeepingActionFailed={onTimekeepingActionFailed}
      />
    );
  }

  return (
    <Stack gap={6}>
      <Box
        sx={{
          padding: 2,
          display: "flex",
          justifyContent: "center",
        }}
        data-testid="timekeeping-shift-progress"
      >
        <ShiftCircleProgress
          startDate={parseISO(shift.start)}
          endDate={shiftEndDate}
          clockInDate={clockInDate}
          clockOutModalState={clockOutModalState}
        />
      </Box>

      {isExtendShiftEnabled && isShiftEndingSoon && !isDefined(existingShiftExtensionRequest) && (
        <SimpleAlert
          severity="warning"
          title="Your shift ends soon"
          description="If you need to stay later, extend your shift before your scheduled clock-out time"
        />
      )}

      {(shift.isInstantPay ?? false) &&
        isMissedPunchRequestEnabledV2 &&
        isDefined(existingProvisionalExtraWorkedTimeRequest) && (
          <InShiftAdjustmentTimesheetReviewAlert />
        )}

      {isDefined(workplaceTimezone) ? (
        <TimekeepingActivityList
          arrivedDate={arrivedDate}
          clockInDate={clockInDate}
          breakStartDate={breakStartDate}
          breakEndDate={breakEndDate}
          workplaceId={workplaceId}
          workplaceTimezone={workplaceTimezone}
          workplaceImage={workplaceImage}
          workplaceName={workplaceName}
          mandatoryBreakDurationInMinutes={
            showMandatoryBreakReminder ? mandatoryBreakDurationInMinutes : undefined
          }
          shiftStartDate={parseISO(shift.start)}
          shiftEndDate={shiftEndDate}
          hasWorkerClockedInOnTime={hasWorkerClockedInOnTime}
          isSecondShiftInDouble={isSecondShiftInDouble}
          isTimesheetEditDisabled={isTimesheetEditDisabled}
          shouldEnforceNfcTimesheetBounds={enforceNfcTimesheetBounds}
          isArrivalTimeFeatureEnabled={useExpectedArrivalTime}
          targetArrivalTime={targetArrivalTime}
          shiftBookedAt={shiftBookedAt}
          policy={clipboardScoreProfile?.policy}
          workerStage={workerStage}
          workerSubstage={workerSubstage?.name}
          isClipboardScoreFreezeEnabled={isClipboardScoreFreezeEnabled}
          clipboardScoreFreezeReason={clipboardScoreFreezeReason}
          adjustedClockInDate={adjustedClockInDate}
          isExtendShiftEnabled={isExtendShiftEnabled}
          shiftExtensionRequest={existingShiftExtensionRequest}
          onEditClockIn={isMissedPunchRequestEnabledV2 ? handleEditClockIn : undefined}
        />
      ) : null}

      <TimekeepingActions
        shift={shift}
        showBreakActivity={showBreakActivity}
        isExtendShiftEnabled={isExtendShiftEnabled}
        hasShiftExtension={isDefined(existingShiftExtensionRequest)}
        onClockOut={onClockOut}
        onExtendShift={() => {
          shiftEndsSoonModalState.openModal();
        }}
        onStartBreak={() => {
          breakStartModalState.openModal();
          logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_START_BREAK_TAPPED, { shiftId: shift._id });
        }}
        onEndBreak={() => {
          breakEndModalState.openModal();
          logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_END_BREAK_TAPPED, { shiftId: shift._id });
        }}
        onSkipBreak={() => {
          skipBreakModalState.openModal();
          logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_SKIP_BREAK_TAPPED, { shiftId: shift._id });
        }}
      />

      {isMissedPunchRequestEnabledV2 && isDefined(clockInDate) && (
        <AdjustTimekeepingBottomSheet
          type="clock-in"
          modalState={adjustClockInModalState}
          originalTime={clockInDate}
          initialTime={adjustedClockInDate}
          existingReason={existingProvisionalExtraWorkedTimeRequest?.attributes.reason}
          workplaceTimezone={workplaceTimezone ?? DEFAULT_TIMEZONE}
          shiftStartAt={parseISO(shift.start)}
          shiftEndAt={parseISO(shift.end)}
          shiftId={shift._id}
          geofenceArrivalTime={geofenceEntryTime}
          earliestPlausibleArrivalTime={earliestPlausibleArrivalAt}
          workplaceSettings={{
            preventEarlyClockIns: !(shiftState.metadata.isEarlyClockInEnabled ?? false),
            earlyClockInMinutes: shiftState.metadata.earlyClockInEnabledInMinutes,
          }}
          isEnforceGeofenceTimestampsEnabled={isEnforceGeofenceTimestampsEnabled}
          workerClockIn={clockInDate}
          isGeofenceWorkplace={isDefined(geofenceEntryTime)}
          isNfcOrMeshWorkplace={
            isMethodEnabled(AttendanceVerificationMethod.NFC_TAG) ||
            isMethodEnabled(AttendanceVerificationMethod.MESH_INTEGRATION)
          }
          onConfirm={(newTime, reason) => {
            const isEarlierThanOriginal = newTime < clockInDate;
            if (isEarlierThanOriginal && isDefined(reason)) {
              if (isDefined(existingProvisionalExtraWorkedTimeRequest)) {
                patchExtraWorkedTimeRequest({
                  shiftId: shift._id,
                  extraWorkedTimeRequestId: existingProvisionalExtraWorkedTimeRequest.id,
                  start: newTime.toISOString(),
                  reason,
                });
              } else {
                createExtraWorkedTimeRequest({
                  shiftId: shift._id,
                  type: ExtraWorkedTimeType.FULL_SHIFT,
                  start: newTime.toISOString(),
                  reason,
                });
              }
            } else if (
              !isEarlierThanOriginal &&
              isDefined(existingProvisionalExtraWorkedTimeRequest)
            ) {
              patchExtraWorkedTimeRequest({
                shiftId: shift._id,
                extraWorkedTimeRequestId: existingProvisionalExtraWorkedTimeRequest.id,
                status: ExtraWorkedTimeRequestStatus.CANCELED,
              });
            }
          }}
        />
      )}

      <BreakActionBottomSheet
        modalState={breakStartModalState}
        shiftId={shift._id}
        shiftStage={ShiftStage.BREAK_START}
        workerId={shift.agentId}
        workplaceId={shift.facilityId}
        workplaceName={shift.facility.name}
        isNfcEnabled={isMethodEnabled(AttendanceVerificationMethod.NFC_TAG)}
        nfcTagRequests={nfcTagRequests}
        isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
      />

      <BreakActionBottomSheet
        modalState={breakEndModalState}
        shiftId={shift._id}
        shiftStage={ShiftStage.BREAK_END}
        workerId={shift.agentId}
        workplaceId={shift.facilityId}
        workplaceName={shift.facility.name}
        isNfcEnabled={isMethodEnabled(AttendanceVerificationMethod.NFC_TAG)}
        nfcTagRequests={nfcTagRequests}
        isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
      />

      <BreakActionBottomSheet
        modalState={skipBreakModalState}
        shiftId={shift._id}
        shiftStage={ShiftStage.SKIP_BREAK}
        workerId={shift.agentId}
        workplaceId={shift.facilityId}
        workplaceName={shift.facility.name}
        // no nfc validation for skip break
        isNfcEnabled={false}
        nfcTagRequests={nfcTagRequests}
        isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
      />

      {getClockOutBottomSheet()}

      <TooFarAwayBottomSheet
        modalState={tooFarAwayModalState}
        workplaceName={shift.facility.name}
        onTryAgain={async () => {
          tooFarAwayModalState.closeModal();
          // If still not inside after retry, user can try again or skip location check
          onClockOut();
        }}
        onSkipLocationCheck={() => {
          tooFarAwayModalState.closeModal();
          acceptNonInstantPayModalState.openModal();
        }}
        onCancel={() => {
          tooFarAwayModalState.closeModal();
        }}
      />

      <AcceptNonInstantPayBottomSheet
        modalState={acceptNonInstantPayModalState}
        isConvertingToNonInstantPay={isConvertingToNonInstantPay}
        isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
        onAccept={handleAcceptNonInstantPay}
        onCancel={() => {
          acceptNonInstantPayModalState.closeModal();
          tooFarAwayModalState.openModal();
        }}
      />

      <SuggestInstantPayBottomSheet
        modalState={suggestInstantPayModalState}
        onClose={() => {
          suggestInstantPayModalState.closeModal();
          onProceedToClockOut();
        }}
      />

      {/* NOTE: This feature is not yet in production. A subsequent PR will handle the case
          where a clock-in adjustment already exists (update instead of create). */}
      <ExtendShiftBottomSheet
        modalState={shiftEndsSoonModalState}
        isSubmitting={isCreatingShiftExtension}
        onSubmit={(reason) => {
          createExtraWorkedTimeRequest(
            {
              shiftId: shift._id,
              type: ExtraWorkedTimeType.FULL_SHIFT,
              start: shift.clockInOut?.start ?? shift.start,
              reason,
            },
            {
              onSuccess: () => {
                shiftEndsSoonModalState.closeModal();
                extendShiftSuccessModalState.openModal();
              },
            }
          );
        }}
      />

      <ExtendShiftSuccessBottomSheet modalState={extendShiftSuccessModalState} />
    </Stack>
  );
}
/* eslint-enable max-lines */
